import type { BadgeProps } from '@/components/Badge/Badge';
import { statusLabel, statusVariant } from '@/components/Badge/statuses';
import type {
    CommissionStatus,
    OrderDateNoteKind,
    OrderLineStatus,
    OrderLineTone,
    OrderListItem,
    OrderOverviewLine,
    OrderSortKey,
    OrderStatus,
    PaymentStatus,
} from '@/features/orders/api';
import { COPY } from '@/features/orders/copy';
import { formatDashedDate, formatDashedDayMonth } from '@/lib/format';

/*
  The register's presentation rules, kept out of the component so they can be tested on their
  own: what each status is called and what colour it takes, how the note under the order date
  reads, and what the export writes.

  Every label and every variant is looked up here and nowhere else. The design's Status
  reference frame gives each status one variant, so a screen that wrote its own would be the
  screen that got it wrong.
*/

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/**
 * The four order statuses the design draws, plus the two the database keeps and the design does
 * not: Placed is retired and carried by no order, and Deleted never reaches the register because
 * those rows are soft-deleted too. Both are here so an unexpected row still renders as itself.
 */
export const ORDER_STATUS: Record<OrderStatus, { label: string; variant: BadgeVariant }> = {
    InProgress: { label: 'In progress', variant: 'secondary' },
    Completed: { label: 'Completed', variant: 'success' },
    Cancelled: { label: 'Cancelled', variant: 'destructive' },
    Placed: { label: 'Placed', variant: 'secondary' },
    Deleted: { label: 'Deleted', variant: 'outline' },
    Unknown: { label: 'Unknown', variant: 'outline' },
};

/**
 * What the client record's Orders card filters by (ALS-169, Franka's call of 04 Sep 2026): the
 * register's order statuses, minus Deleted, which a client's own list never shows.
 */
export const CLIENT_ORDER_STATUSES = [
    'InProgress',
    'Completed',
    'Cancelled',
] as const satisfies readonly OrderStatus[];

export type ClientOrderStatus = (typeof CLIENT_ORDER_STATUSES)[number];

/**
 * The six payment statuses, from the design's Status reference frame. Refunded is the server's
 * reading of a cancelled order that was paid (ADR 0049): the legacy leaves PaymentCompleted set
 * when it unwinds an order, so its own derivation calls every one of those Fully paid.
 */
export const PAYMENT_STATUS: Record<PaymentStatus, { label: string; variant: BadgeVariant }> = {
    FullyPaid: { label: 'Fully paid', variant: 'success' },
    PartiallyPaid: { label: 'Partially paid', variant: 'warning' },
    DepositPaid: { label: 'Deposit paid', variant: 'warning' },
    DepositUnpaid: { label: 'Deposit unpaid', variant: 'destructive' },
    NotPaid: { label: 'Not paid', variant: 'destructive' },
    Refunded: { label: 'Refunded', variant: 'destructive' },
};

/**
 * The order's commission status (ALS-288). Not paid is the neutral badge, not the payment status's
 * pink: every commission order starts there and waits on the suppliers, so it is the normal state
 * rather than an error. Paid is green.
 */
export const COMMISSION_STATUS: Record<CommissionStatus, { label: string; variant: BadgeVariant }> =
    {
        NotPaid: { label: 'Not paid', variant: 'secondary' },
        Paid: { label: 'Paid', variant: 'success' },
    };

/** The two, in the order the select and the filter list them. */
export const COMMISSION_STATUSES: readonly CommissionStatus[] = ['NotPaid', 'Paid'];

/**
 * The dot on a product thumbnail: 11px, no ring, on the tile's corner. The word is in the
 * tooltip, so the colour is the whole signal here and the tone comes from the server.
 */
export const LINE_TONE_DOT: Record<OrderLineTone, string> = {
    InFlight: 'bg-primary',
    Done: 'bg-success',
    Attention: 'bg-warning',
    Adverse: 'bg-destructive',
    Neutral: 'bg-muted-foreground',
};

/**
 * The status in words under a dashboard thumbnail, tinted as its dot is, so the caption and the
 * dot never disagree about a line (the prototype's status-text tones).
 */
export const LINE_TONE_TEXT: Record<OrderLineTone, string> = {
    InFlight: 'text-primary',
    Done: 'text-success',
    Attention: 'text-warning',
    Adverse: 'text-destructive',
    Neutral: 'text-muted-foreground',
};

/**
 * The badge a line's status takes in the expanded row, from the same tone as its dot, so the
 * tile and the badge never disagree about a line.
 */
export const LINE_TONE_BADGE: Record<OrderLineTone, BadgeVariant> = {
    InFlight: 'secondary',
    Done: 'success',
    Attention: 'warning',
    Adverse: 'destructive',
    Neutral: 'outline',
};

/**
 * The design's value for each line status the API names, which the Status reference frame then
 * labels and colours (components/Badge/statuses). One table for the whole admin: the register's
 * thumbnails and expanded row, its filter drawer and the order's own page all read a line's
 * status through it, so "Products secured" reads Secured wherever it appears (ALS-61).
 *
 * The two retired rows old lines still carry take the words the design has for them in its order
 * and payment vocabularies. Exchange, the trunk-show ladder and a row this build does not know
 * have no word in the design at all, and read by their legacy names.
 */
const LINE_STATUS_VALUE: Partial<Record<OrderLineStatus, string>> = {
    Pending: 'Pending',
    AwaitingPayment: 'Awaiting payment',
    Confirmed: 'Confirmed',
    Secured: 'Secured',
    Received: 'Received',
    Shipped: 'Shipped',
    Delivered: 'Delivered',
    ExchangeRequested: 'Exchange requested',
    ExchangeInProgress: 'Exchange in progress',
    ExchangeShipped: 'Exchange shipped',
    ExchangeDelivered: 'Exchange delivered',
    CancellationRequested: 'Cancellation requested',
    CancellationConfirmed: 'Cancellation confirmed',
    CancellationRefunded: 'Cancellation refunded',
    ReturnRequested: 'Return requested',
    RefundInProgress: 'Refund in progress',
    RefundCompleted: 'Refund completed',
    Deleted: 'Deleted',
    Cancelled: 'Cancelled',
    Refunded: 'Refunded',
};

export interface LineStatusLook {
    label: string;
    variant: BadgeVariant;
}

/**
 * How a line's status reads and what colour its badge takes. A status the design names reads its
 * way; one it does not reads by its legacy name, coloured by the tone the server gave it, so a
 * trunk-show line still says which rung it is on.
 */
export function lineStatusLook(
    status: OrderLineStatus,
    legacyName: string,
    tone: OrderLineTone,
): LineStatusLook {
    const value = LINE_STATUS_VALUE[status];
    return value
        ? { label: statusLabel(value), variant: statusVariant(value) }
        : { label: legacyText(legacyName), variant: LINE_TONE_BADGE[tone] };
}

/** Only the words, for a tooltip, a screen reader or a select's option. */
export function lineStatusText(status: OrderLineStatus, legacyName: string): string {
    const value = LINE_STATUS_VALUE[status];
    return value ? statusLabel(value) : legacyText(legacyName);
}

/**
 * A legacy name in the sentence case every other status reads in: the register wrote "Trunk Show"
 * beside "Trunk show paid in full" (ALS-296). A capitalised word after the first is lowered; one in
 * capitals throughout, an initialism, is left as it is.
 */
function legacyText(name: string): string {
    return name.replace(/(?<=\s)([A-Z])(?=[a-z])/g, (letter) => letter.toLowerCase());
}

/** Which of the expanded row's two sections are open. The page keeps it, so it holds between rows. */
export interface OrderSections {
    products: boolean;
    order: boolean;
}

export const ALL_SECTIONS_OPEN: OrderSections = { products: true, order: true };

/**
 * What the expanded row prints for a line's commission: the rate it resolved to on the order
 * date, "Yes" when the line carries commission but no rate applied that day, and null for a line
 * with none, which the screen writes as a greyed "None".
 */
export function commissionText(
    line: Pick<OrderOverviewLine, 'commission' | 'commissionRate'>,
): string | null {
    if (!line.commission) return null;
    if (line.commissionRate === null) return COPY.expansion.yes;
    return `${line.commissionRate.toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`;
}

/**
 * How near a due date has to be to read as urgent. A week: near enough that somebody should be
 * chasing it, far enough that a Monday morning still shows what Friday needs.
 */
export const DUE_SOON_DAYS = 7;

/** What the note under the order date is saying, once the due date has been measured against today. */
export type DueUrgency = 'overdue' | 'soon' | 'none';

export interface OrderDateNote {
    /** The line itself, "due 20-Jun" or "overdue 20-Jun". */
    text: string;
    urgency: DueUrgency;
}

const NOTE_WORD: Record<Exclude<OrderDateNoteKind, 'None'>, string> = {
    Due: 'due',
    Delivered: 'delivered',
    Cancelled: 'cancelled',
};

/**
 * The second line under the order date, as the design draws it: a due date, a delivered date or
 * a cancelled date, whichever the order's status calls for, and nothing at all when the date
 * behind it was never recorded.
 *
 * Urgency is worked out here rather than on the server, because it is a fact about today rather
 * than about the order, and a page sitting open overnight should not keep yesterday's answer.
 *
 * The word carries the urgency as well as the colour does: an overdue row reads "overdue 20-Jun"
 * and one falling due this week reads "due soon 20-Jun", so the three are told apart with the
 * colour switched off (ALS-57). The day is written the prototype's way, as the order date is
 * (ALS-223).
 */
export function dateNote(order: OrderListItem, today: Date = new Date()): OrderDateNote | null {
    if (order.noteKind === 'None' || order.noteDate === null) return null;

    const when = new Date(order.noteDate);
    if (Number.isNaN(when.getTime())) return null;

    const day = formatDashedDayMonth(when);
    if (order.noteKind !== 'Due') {
        return { text: `${NOTE_WORD[order.noteKind]} ${day}`, urgency: 'none' };
    }

    const urgency = dueUrgency(when, today);
    const word = urgency === 'overdue' ? 'overdue' : urgency === 'soon' ? 'due soon' : 'due';
    return { text: `${word} ${day}`, urgency };
}

/**
 * The colour the note takes. Only a due date has an urgency, so the other two stay quiet.
 *
 * The two that are not quiet are marked important, which is load bearing rather than lazy. The
 * note is the second line of a `TableCellStack`, and that component mutes its second child
 * through `[&>:nth-child(2)]:text-muted-foreground` on the parent. That compiles to a selector
 * of specificity (0,2,0), which beats a plain `text-destructive` on the child at (0,1,0), so
 * without the override every note renders muted and the urgency is invisible.
 */
export const DUE_URGENCY_CLASS: Record<DueUrgency, string> = {
    overdue: 'text-destructive!',
    soon: 'text-warning!',
    // Muted, which is what the stack already gives its second line.
    none: '',
};

function dueUrgency(due: Date, today: Date): DueUrgency {
    const days = wholeDaysBetween(today, due);
    if (days < 0) return 'overdue';
    return days <= DUE_SOON_DAYS ? 'soon' : 'none';
}

/** Calendar days from one date to the other, ignoring the time of day on either. */
function wholeDaysBetween(from: Date, to: Date): number {
    const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
    const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((b - a) / 86_400_000);
}

/**
 * The export's columns, in the order the table shows them, which is what the ticket asks for.
 * The products cell becomes the line names it stands for, and the date cell's two lines become
 * two columns so a spreadsheet can sort on either.
 */
export const ORDER_CSV_HEADER = [
    'Products',
    'Customer',
    'Order no',
    'Items',
    'Order date',
    'Due',
    'Net amount',
    'Currency',
    'Net amount (GBP)',
    'Payment',
    'Order status',
];

/**
 * One CSV line per order, dates written as the register writes them.
 *
 * The products column names every product on the order: the export asks the API for all of an
 * order's lines, not the four the table's thumbnails need, because the CSV is the only way to
 * take the register into a spreadsheet and "+13 more" lost the rest there (ALS-223, which the
 * prototype's own export settles). Should a row still arrive short, it says how many it could not
 * name rather than passing for a smaller order.
 */
export function orderCsvRows(orders: OrderListItem[], today: Date = new Date()): string[][] {
    return orders.map((order) => [
        productNames(order),
        order.customer,
        order.orderNo,
        String(order.itemCount),
        formatDashedDate(order.orderDate),
        dateNote(order, today)?.text ?? '',
        String(order.net),
        order.currency,
        String(order.netBase),
        PAYMENT_STATUS[order.payment].label,
        ORDER_STATUS[order.status].label,
    ]);
}

function productNames(order: OrderListItem): string {
    const names = order.lines.map((line) => line.name);
    const rest = order.itemCount - names.length;
    return rest > 0 ? `${names.join('; ')}; +${rest} more` : names.join('; ');
}

export const ORDER_SORT_OPTIONS: { key: OrderSortKey; label: string }[] = [
    { key: 'customer', label: COPY.sort.customer },
    { key: 'date', label: COPY.sort.date },
    { key: 'net', label: COPY.sort.net },
    { key: 'payment', label: COPY.sort.payment },
    { key: 'status', label: COPY.sort.status },
];

const ORDER_SORT_KEYS: readonly OrderSortKey[] = ORDER_SORT_OPTIONS.map((option) => option.key);

export function isOrderSortKey(value: string): value is OrderSortKey {
    return (ORDER_SORT_KEYS as readonly string[]).includes(value);
}

/** The company's base currency, which the second amount line converts to. */
export const BASE_CURRENCY = 'GBP';
